import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Bot,
  BrainCircuit,
  Check,
  Code2,
  Globe2,
  Languages,
  MessageCircle,
  Moon,
  Rocket,
  ShieldCheck,
  Sparkles,
  Sun,
  Workflow,
} from 'lucide-react';
import './App.css';
import logo from './assets/wardie_logo.png';
import tdcLogo from './assets/clients/tdc-logo.png';
import justinGelbandLogo from './assets/clients/justin-gelband-logo.png';
import smtCmmsLogo from './assets/clients/smt-cmms-logo.png';
import tsuruLogo from './assets/clients/tsuru-logo.png';
import edipediaLogo from './assets/clients/edipedia-logo.png';
import lerOMundoLogo from './assets/clients/ler-o-mundo-logo.png';

const clients = [
  { name: 'Técnicos de Celular', logo: tdcLogo },
  { name: 'Justin Gelband', logo: justinGelbandLogo },
  { name: 'SMTCMMS', logo: smtCmmsLogo },
  { name: 'Coletivo Tsuru', logo: tsuruLogo },
  { name: 'Edipedia', logo: edipediaLogo },
  { name: 'Ler o Mundo', logo: lerOMundoLogo },
];

const copy = {
  pt: {
    languageName: 'Português',
    nav: ['Wardie IA', 'Serviços', 'Processo', 'Clientes', 'Contato'],
    hero: {
      eyebrow: 'Software sob medida, IA aplicada e produto digital',
      title: 'Tecnologia que transforma atendimento, operação e crescimento.',
      text: 'A Wardie cria sites, sistemas, aplicativos e agentes de IA para empresas que querem vender melhor, automatizar rotinas e entregar experiências digitais memoráveis.',
      primary: 'Solicitar demonstração',
      secondary: 'Ver soluções',
    },
    stats: [
      ['24h', 'atendimento inteligente'],
      ['100%', 'soluções sob medida'],
    ],
    ai: {
      badge: 'Novo destaque',
      title: 'Wardie IA: sua secretária no WhatsApp, pronta 24 horas.',
      text: 'Imagine seu cliente chamando tarde da noite para marcar um serviço. Em vez de esperar a manhã seguinte ou depender de alguém sempre online, ele conversa com uma secretária de IA treinada para entender sua empresa, responder com clareza, organizar pedidos e conduzir o agendamento. O atendimento continua humano no cuidado, mas ganha a velocidade que o cliente espera hoje.',
      closing: 'Com o Wardie IA, sua operação não perde oportunidades enquanto você dorme, atende outro cliente ou toca o negócio. Solicite uma demonstração e veja seu agente personalizado trabalhando em poucos minutos.',
      cta: 'Quero uma demonstração',
      bullets: ['Atende pelo WhatsApp', 'Responde 24 horas', 'Agenda serviços', 'Personalizado para sua empresa'],
    },
    services: {
      eyebrow: 'O que construímos',
      title: 'Soluções digitais para empresas que precisam sair do improviso.',
      items: [
        ['Sites institucionais', 'Presença digital moderna, rápida e preparada para converter visitantes em conversas.'],
        ['Sistemas web', 'Ferramentas internas para organizar processos, reduzir retrabalho e dar visibilidade ao negócio.'],
        ['Aplicativos', 'Experiências mobile pensadas para clientes, equipes e operações em movimento.'],
        ['Consultoria técnica', 'Planejamento, arquitetura e evolução de produtos digitais com visão de negócio.'],
      ],
    },
    process: {
      eyebrow: 'Como trabalhamos',
      title: 'Da ideia ao lançamento com clareza, ritmo e acabamento.',
      steps: [
        ['Descoberta', 'Mapeamos objetivos, dores, públicos e oportunidades reais antes de desenhar a solução.'],
        ['Prototipação', 'Transformamos a estratégia em fluxos, telas e experiências que podem ser validadas cedo.'],
        ['Desenvolvimento', 'Construímos com tecnologia atual, integrações úteis, performance e segurança.'],
        ['Evolução', 'Acompanhamos métricas, refinamos jornadas e abrimos espaço para automação com IA.'],
      ],
    },
    clients: {
      eyebrow: 'Confiança',
      title: 'Marcas e projetos que já caminharam com a Wardie.',
    },
    contact: {
      title: 'Vamos criar algo que trabalhe pela sua empresa.',
      text: 'Conte o que você quer melhorar. A gente ajuda a transformar a ideia em uma solução bonita, útil e pronta para crescer.',
      whatsapp: 'Conversar no WhatsApp',
      email: 'Enviar e-mail',
      footer: 'Wardie Development',
    },
  },
  en: {
    languageName: 'English',
    nav: ['Wardie AI', 'Services', 'Process', 'Clients', 'Contact'],
    hero: {
      eyebrow: 'Custom software, applied AI and digital product',
      title: 'Technology that upgrades support, operations and growth.',
      text: 'Wardie builds websites, systems, apps and AI agents for companies that want to sell better, automate routines and deliver memorable digital experiences.',
      primary: 'Request a demo',
      secondary: 'See solutions',
    },
    stats: [
      ['24/7', 'intelligent support'],
      ['100%', 'tailored solutions'],
    ],
    ai: {
      badge: 'Featured now',
      title: 'Wardie AI: your WhatsApp secretary, ready 24/7.',
      text: 'Picture a customer messaging late at night to book a service. Instead of waiting until morning or relying on someone to stay online, they talk to an AI secretary trained to understand your company, answer clearly, organize requests and guide the booking. The care still feels human, but the speed matches what customers expect today.',
      closing: 'With Wardie AI, your operation stops missing opportunities while you sleep, serve another client or run the business. Request a demo and watch your custom agent working in minutes.',
      cta: 'I want a demo',
      bullets: ['Works on WhatsApp', 'Replies 24/7', 'Books services', 'Customized for your company'],
    },
    services: {
      eyebrow: 'What we build',
      title: 'Digital solutions for companies ready to move beyond improvisation.',
      items: [
        ['Institutional websites', 'Modern, fast digital presence designed to turn visitors into conversations.'],
        ['Web systems', 'Internal tools that organize processes, reduce rework and make operations visible.'],
        ['Apps', 'Mobile experiences designed for customers, teams and operations on the move.'],
        ['Technical consulting', 'Planning, architecture and digital product evolution with business perspective.'],
      ],
    },
    process: {
      eyebrow: 'How we work',
      title: 'From idea to launch with clarity, pace and polish.',
      steps: [
        ['Discovery', 'We map goals, pain points, audiences and real opportunities before designing the solution.'],
        ['Prototyping', 'We turn strategy into flows, screens and experiences that can be validated early.'],
        ['Development', 'We build with current technology, useful integrations, performance and security.'],
        ['Evolution', 'We follow metrics, refine journeys and create room for AI automation.'],
      ],
    },
    clients: {
      eyebrow: 'Trust',
      title: 'Brands and projects that have already moved with Wardie.',
    },
    contact: {
      title: 'Let’s build something that works for your company.',
      text: 'Tell us what you want to improve. We help turn the idea into a beautiful, useful solution ready to grow.',
      whatsapp: 'Talk on WhatsApp',
      email: 'Send email',
      footer: 'Wardie Development',
    },
  },
  es: {
    languageName: 'Español',
    nav: ['Wardie IA', 'Servicios', 'Proceso', 'Clientes', 'Contacto'],
    hero: {
      eyebrow: 'Software a medida, IA aplicada y producto digital',
      title: 'Tecnología que mejora atención, operación y crecimiento.',
      text: 'Wardie crea sitios, sistemas, aplicaciones y agentes de IA para empresas que quieren vender mejor, automatizar rutinas y entregar experiencias digitales memorables.',
      primary: 'Solicitar demostración',
      secondary: 'Ver soluciones',
    },
    stats: [
      ['24h', 'atención inteligente'],
      ['100%', 'soluciones a medida'],
    ],
    ai: {
      badge: 'Nuevo destaque',
      title: 'Wardie IA: tu secretaria en WhatsApp, lista 24 horas.',
      text: 'Imagina a un cliente escribiendo tarde por la noche para reservar un servicio. En lugar de esperar hasta la mañana o depender de alguien siempre conectado, conversa con una secretaria de IA entrenada para entender tu empresa, responder con claridad, organizar solicitudes y guiar la reserva. La atención mantiene el cuidado humano, pero gana la velocidad que el cliente espera hoy.',
      closing: 'Con Wardie IA, tu operación deja de perder oportunidades mientras duermes, atiendes a otro cliente o diriges el negocio. Solicita una demostración y mira a tu agente personalizado trabajando en minutos.',
      cta: 'Quiero una demostración',
      bullets: ['Atiende por WhatsApp', 'Responde 24 horas', 'Agenda servicios', 'Personalizada para tu empresa'],
    },
    services: {
      eyebrow: 'Lo que construimos',
      title: 'Soluciones digitales para empresas listas para dejar la improvisación.',
      items: [
        ['Sitios institucionales', 'Presencia digital moderna, rápida y preparada para convertir visitantes en conversaciones.'],
        ['Sistemas web', 'Herramientas internas para organizar procesos, reducir retrabajo y dar visibilidad al negocio.'],
        ['Aplicaciones', 'Experiencias móviles pensadas para clientes, equipos y operaciones en movimiento.'],
        ['Consultoría técnica', 'Planificación, arquitectura y evolución de productos digitales con visión de negocio.'],
      ],
    },
    process: {
      eyebrow: 'Cómo trabajamos',
      title: 'De la idea al lanzamiento con claridad, ritmo y acabado.',
      steps: [
        ['Descubrimiento', 'Mapeamos objetivos, dolores, públicos y oportunidades reales antes de diseñar la solución.'],
        ['Prototipado', 'Transformamos la estrategia en flujos, pantallas y experiencias que pueden validarse temprano.'],
        ['Desarrollo', 'Construimos con tecnología actual, integraciones útiles, rendimiento y seguridad.'],
        ['Evolución', 'Seguimos métricas, refinamos recorridos y abrimos espacio para automatización con IA.'],
      ],
    },
    clients: {
      eyebrow: 'Confianza',
      title: 'Marcas y proyectos que ya caminaron con Wardie.',
    },
    contact: {
      title: 'Construyamos algo que trabaje por tu empresa.',
      text: 'Cuéntanos qué quieres mejorar. Te ayudamos a convertir la idea en una solución bonita, útil y lista para crecer.',
      whatsapp: 'Hablar por WhatsApp',
      email: 'Enviar email',
      footer: 'Wardie Development',
    },
  },
};

const sectionVariants = {
  hidden: { opacity: 0, y: 34 },
  visible: { opacity: 1, y: 0 },
};

const fadeIn = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const icons = [Code2, Workflow, Rocket, BrainCircuit];

function App() {
  const [theme, setTheme] = useState('dark');
  const [language, setLanguage] = useState('pt');
  const t = copy[language];
  const year = useMemo(() => new Date().getFullYear(), []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="App" data-theme={theme}>
      <header className="siteHeader">
        <button className="brandButton" type="button" onClick={() => scrollTo('inicio')} aria-label="Wardie">
          <img src={logo} alt="Wardie" />
          <span>Wardie</span>
        </button>

        <nav className="siteNav" aria-label="Principal">
          {t.nav.map((item, index) => {
            const ids = ['wardie-ia', 'servicos', 'processo', 'clientes', 'contato'];
            return (
              <button key={item} type="button" onClick={() => scrollTo(ids[index])}>
                {item}
              </button>
            );
          })}
        </nav>

        <div className="headerActions">
          <label className="selectControl" aria-label="Idioma">
            <Languages size={17} />
            <select value={language} onChange={(event) => setLanguage(event.target.value)}>
              {Object.entries(copy).map(([key, value]) => (
                <option key={key} value={key}>
                  {value.languageName}
                </option>
              ))}
            </select>
          </label>

          <button
            className="iconButton"
            type="button"
            onClick={() => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))}
            aria-label={theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </header>

      <main>
        <section className="heroSection" id="inicio">
          <div className="heroContent">
            <motion.p className="eyebrow" initial="hidden" animate="visible" variants={fadeIn} transition={{ duration: 0.6 }}>
              <Sparkles size={16} />
              {t.hero.eyebrow}
            </motion.p>
            <motion.h1 initial="hidden" animate="visible" variants={fadeIn} transition={{ duration: 0.7, delay: 0.08 }}>
              {t.hero.title}
            </motion.h1>
            <motion.p className="heroText" initial="hidden" animate="visible" variants={fadeIn} transition={{ duration: 0.7, delay: 0.16 }}>
              {t.hero.text}
            </motion.p>
            <motion.div className="heroActions" initial="hidden" animate="visible" variants={fadeIn} transition={{ duration: 0.7, delay: 0.24 }}>
              <a className="primaryButton" href="https://api.whatsapp.com/send?phone=5585989284862" target="_blank" rel="noreferrer">
                {t.hero.primary}
                <ArrowRight size={18} />
              </a>
              <button className="secondaryButton" type="button" onClick={() => scrollTo('servicos')}>
                {t.hero.secondary}
              </button>
            </motion.div>
          </div>

          <motion.div
            className="heroVisual"
            initial={{ opacity: 0, scale: 0.94, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, delay: 0.12, ease: 'easeOut' }}
          >
            <div className="logoStage">
              <motion.img src={logo} alt="Wardie" animate={{ y: [0, -10, 0] }} transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }} />
            </div>
            <div className="signalPanel">
              {t.stats.map(([number, label]) => (
                <span key={label}>
                  <strong>{number}</strong>
                  {label}
                </span>
              ))}
            </div>
          </motion.div>
        </section>

        <motion.section
          className="aiSection"
          id="wardie-ia"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7 }}
        >
          <div className="sectionIntro">
            <p className="eyebrow">
              <Bot size={16} />
              {t.ai.badge}
            </p>
            <h2>{t.ai.title}</h2>
          </div>
          <div className="aiStory">
            <p>{t.ai.text}</p>
            <p>{t.ai.closing}</p>
            <div className="aiBullets">
              {t.ai.bullets.map((item) => (
                <span key={item}>
                  <Check size={16} />
                  {item}
                </span>
              ))}
            </div>
            <a className="primaryButton" href="https://api.whatsapp.com/send?phone=5585989284862" target="_blank" rel="noreferrer">
              {t.ai.cta}
              <MessageCircle size={18} />
            </a>
          </div>
        </motion.section>

        <motion.section
          className="contentSection"
          id="servicos"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7 }}
        >
          <div className="sectionIntro">
            <p className="eyebrow">
              <Globe2 size={16} />
              {t.services.eyebrow}
            </p>
            <h2>{t.services.title}</h2>
          </div>
          <div className="serviceGrid">
            {t.services.items.map(([title, text], index) => {
              const Icon = icons[index];
              return (
                <motion.article className="serviceCard" key={title} whileHover={{ y: -8 }} transition={{ type: 'spring', stiffness: 240, damping: 18 }}>
                  <Icon size={28} />
                  <h3>{title}</h3>
                  <p>{text}</p>
                </motion.article>
              );
            })}
          </div>
        </motion.section>

        <motion.section
          className="contentSection processSection"
          id="processo"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7 }}
        >
          <div className="sectionIntro">
            <p className="eyebrow">
              <ShieldCheck size={16} />
              {t.process.eyebrow}
            </p>
            <h2>{t.process.title}</h2>
          </div>
          <div className="timeline">
            {t.process.steps.map(([title, text], index) => (
              <article className="timelineItem" key={title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="contentSection clientsSection"
          id="clientes"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7 }}
        >
          <div className="sectionIntro">
            <p className="eyebrow">{t.clients.eyebrow}</p>
            <h2>{t.clients.title}</h2>
          </div>
          <div className="clientGrid">
            {clients.map((client) => (
              <article className="clientCard" key={client.name}>
                <img src={client.logo} alt={client.name} />
                <span>{client.name}</span>
              </article>
            ))}
          </div>
        </motion.section>

        <section className="contactSection" id="contato">
          <div>
            <p className="eyebrow">
              <MessageCircle size={16} />
              {t.nav[4]}
            </p>
            <h2>{t.contact.title}</h2>
            <p>{t.contact.text}</p>
          </div>
          <div className="contactActions">
            <a className="primaryButton" href="https://api.whatsapp.com/send?phone=5585989284862" target="_blank" rel="noreferrer">
              {t.contact.whatsapp}
              <MessageCircle size={18} />
            </a>
            <a className="secondaryButton" href="mailto:howard@wardie.com.br">
              {t.contact.email}
            </a>
          </div>
        </section>
      </main>

      <footer className="siteFooter">
        <span>{t.contact.footer} © {year}</span>
        <span>LinkedIn · Instagram · WhatsApp</span>
      </footer>
    </div>
  );
}

export default App;
